import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  StaleCandidateKind,
  useShippingStaleSync,
} from "@/hooks/useShippingStaleSync";
import { ShippingStaleSyncBanner } from "@/components/ShippingStaleSyncBanner";
import { ShippingStaleSyncModal } from "@/components/ShippingStaleSyncModal";
import type { StaleCandidate } from "@/hooks/useShippingStaleSync";

// 후보 내용 지문 — stored/synthesized/modifiedAt 중 하나라도 바뀌면 다른 후보로 취급.
// 체크는 이 지문과 함께 저장되어, 재스캔으로 내용이 바뀐 행의 체크를 자동 해제한다.
const fingerprintOf = (c: StaleCandidate): string =>
  `${c.modifiedAt ?? ""}|${c.stored ?? ""}|${c.synthesized}`;

// stale 일괄 반영의 UI 상태(모달 open·체크박스)를 페이지 밖으로 격리하는 컨테이너.
// 배너(트리거)가 컨테이너 안에 함께 있으므로 ref.open() 노출도 불필요 — 페이지는
// 데이터 props와 콜백 3개만 넘긴다. React.memo 금지(안티블로트 규율 3 — stale 함정).
//
// 미리보기 무효화 계약(플랜 codexloop 합의): 날짜/유저/게이트/템플릿 변경 시 모달을 닫고
// 선택을 버린다. 반영 중에는 닫기·재선택·다운로드가 막힌다(onBusyChange로 페이지에 전파).

interface ShippingStaleSyncContainerProps {
  userId: string | null;
  /** yyyy-MM-dd */
  orderDate: string;
  parseProductName: boolean;
  /** 템플릿 편집/전환도 미리보기 무효화 트리거 — id+modifiedAt 기반 키(이름만으론 편집 미감지) */
  templateKey: string | null;
  /** 같은 스코프의 페이지측 변경(인라인 상품명 저장 등) 시 증가 — 재스캔 트리거 */
  refreshToken: number;
  onApplied: (rows: { parsedOrderId: number; newValue: string }[]) => void;
  /** commit-ambiguous 복구 시 DB 확정값으로 페이지 rows 재조정 */
  onReconciled: (rows: { parsedOrderId: number; productName: string | null }[]) => void;
  onBusyChange: (busy: boolean) => void;
}

export function ShippingStaleSyncContainer({
  userId,
  orderDate,
  parseProductName,
  templateKey,
  refreshToken,
  onApplied,
  onReconciled,
  onBusyChange,
}: ShippingStaleSyncContainerProps) {
  const { scanState, candidates, isApplying, rescan, apply } = useShippingStaleSync({
    userId,
    orderDate,
    parseProductName,
    refreshToken,
    onApplied,
    onReconciled,
  });

  const [modalOpen, setModalOpen] = useState(false);
  // 체크는 id만이 아니라 "체크 당시 후보 내용의 지문"과 함께 저장한다(델타 리뷰 #1).
  // 재스캔으로 같은 id의 후보 내용(저장값/합성값/수정시각)이 바뀌면 체크를 해제해,
  // 셀러가 못 본 새 미리보기로 반영이 나가는 일을 막는다.
  const [checked, setChecked] = useState<Map<number, string>>(new Map());

  // 레이아웃 안정화 (오너 피드백 2026-07-29 — 반영 시 화면이 출렁임):
  // 훅은 안전 계약상 스캔 시작 시 후보를 즉시 비우지만, "표시"까지 비우면
  // ① 모달 목록이 비었다 다시 차고 ② 배너가 사라졌다 재등장해 뒤 테이블이 밀린다.
  // 마지막 ready 시점의 후보를 스냅샷해 스캔 동안 그대로 보여주고,
  // 안전은 표시가 아니라 "반영 버튼 비활성(scanState !== 'ready')"으로 지킨다 —
  // apply는 항상 훅의 live 후보로만 실행되므로 낡은 표시로 낡은 쓰기가 나갈 수 없다.
  const lastReadyRef = useRef<typeof candidates>([]);
  useEffect(() => {
    if (scanState === "ready") {
      lastReadyRef.current = candidates;
    }
  }, [scanState, candidates]);
  const scanning = scanState === "scanning";
  const displayCandidates = scanning || isApplying ? lastReadyRef.current : candidates;
  const bannerCount = scanning ? lastReadyRef.current.length : candidates.length;

  // 재스캔 확정(ready) 시 체크 정리: 사라진 행, 내용이 바뀐 행의 체크를 해제한다.
  // 반영 직후(applied 제거)와 무해한 재스캔(내용 동일)에서는 체크가 그대로 살아남는다.
  useEffect(() => {
    if (scanState !== "ready") return;
    setChecked((prev) => {
      if (prev.size === 0) return prev;
      const liveById = new Map(candidates.map((c) => [c.parsedOrderId, fingerprintOf(c)]));
      let changed = false;
      const next = new Map(prev);
      prev.forEach((fp, id) => {
        if (liveById.get(id) !== fp) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [scanState, candidates]);

  // 모달이 열린 채 백그라운드 스캔이 실패하면 모달을 닫아 실패 배너를 드러낸다(가시적 실패 계약).
  useEffect(() => {
    if (scanState === "failed" && modalOpen && !isApplying) {
      setModalOpen(false);
      setChecked(new Map());
      toast.error("배송정보 확인에 실패했습니다.\n다시 확인이 필요합니다.");
    }
  }, [scanState, modalOpen, isApplying]);

  // 반영 중 상태를 페이지로 전파 — 다운로드 버튼 비활성용.
  // 언마운트(로그아웃 등) 시 busy를 반드시 해제 — 페이지에 true가 고아로 남으면
  // 게스트 다운로드까지 영구히 막힌다(PR2 R1 지적). 콜백은 latest-ref로.
  const onBusyChangeRef = useRef(onBusyChange);
  useLayoutEffect(() => {
    onBusyChangeRef.current = onBusyChange;
  });
  useEffect(() => {
    onBusyChangeRef.current(isApplying);
  }, [isApplying]);
  useEffect(() => {
    return () => {
      onBusyChangeRef.current(false);
    };
  }, []);

  // 무효화 키: 날짜/유저/게이트/템플릿이 바뀌면 열린 미리보기는 더 이상 유효하지 않다.
  const invalidationKey = `${userId ?? ""}|${orderDate}|${parseProductName}|${templateKey ?? ""}`;
  const prevKeyRef = useRef(invalidationKey);
  useEffect(() => {
    if (prevKeyRef.current !== invalidationKey) {
      prevKeyRef.current = invalidationKey;
      setModalOpen(false);
      setChecked(new Map());
    }
  }, [invalidationKey]);

  const handleOpen = useCallback(() => {
    // 열 때마다 기본 체크 정책으로 초기화 (오너 결정: 항목 추가 중 휴리스틱 통과분만).
    setChecked(
      new Map(
        candidates
          .filter((c) => c.defaultChecked)
          .map((c) => [c.parsedOrderId, fingerprintOf(c)])
      )
    );
    setModalOpen(true);
  }, [candidates]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setModalOpen(open);
      if (!open) {
        // 닫으면 최신 상태로 재동기화 — conflict 주석은 다음 스캔 결과로 대체된다.
        setChecked(new Map());
        rescan();
      }
    },
    [rescan]
  );

  // 토글/그룹 선택은 live 후보에서 지문을 읽는다 — 컨트롤은 scanState==='ready'일 때만
  // 활성이므로(모달 selectionEnabled) live와 표시가 항상 같은 목록이다.
  const handleToggle = useCallback(
    (parsedOrderId: number) => {
      setChecked((prev) => {
        const next = new Map(prev);
        if (next.has(parsedOrderId)) {
          next.delete(parsedOrderId);
        } else {
          const live = candidates.find((c) => c.parsedOrderId === parsedOrderId);
          if (!live) return prev;
          next.set(parsedOrderId, fingerprintOf(live));
        }
        return next;
      });
    },
    [candidates]
  );

  const handleGroupSet = useCallback(
    (kind: StaleCandidateKind, value: boolean) => {
      setChecked((prev) => {
        const next = new Map(prev);
        candidates
          .filter((c) => c.kind === kind && c.applyEligible)
          .forEach((c) => {
            if (value) next.set(c.parsedOrderId, fingerprintOf(c));
            else next.delete(c.parsedOrderId);
          });
        return next;
      });
    },
    [candidates]
  );

  const handleApply = useCallback(async () => {
    // 쓰기 경계 지문 재검증(델타 R2-#2): 정리 effect는 커밋 한 박자 뒤에 돌므로,
    // "재스캔 커밋 직후 정리 전" 창에서의 클릭이 낡은 체크로 반영되는 것을 여기서 막는다.
    // 하나라도 어긋나면 반영하지 않고 해당 체크만 풀어 다시 확인하게 한다.
    const liveById = new Map(candidates.map((c) => [c.parsedOrderId, fingerprintOf(c)]));
    const staleCheckIds: number[] = [];
    checked.forEach((fp, id) => {
      if (liveById.get(id) !== fp) staleCheckIds.push(id);
    });
    if (staleCheckIds.length > 0) {
      setChecked((prev) => {
        const next = new Map(prev);
        staleCheckIds.forEach((id) => next.delete(id));
        return next;
      });
      toast.warning(
        `내용이 바뀐 배송정보 ${staleCheckIds.length}건이 선택에서 제외되었습니다.\n확인 후 다시 반영이 가능합니다.`
      );
      return;
    }

    const result = await apply(Array.from(checked.keys()));

    if (result.type === "stale-scope") {
      // 스코프 변경/언마운트 중 반영·중복 호출 — 무효화 effect가 모달을 이미 닫았거나 곧 닫는다.
      return;
    }
    if (result.type === "ambiguous") {
      // 후보 폐기 + 복구 스캔(rows 재조정)까지 훅이 끝냈다. 미리보기는 더 이상 유효하지 않다.
      setModalOpen(false);
      setChecked(new Map());
      toast.error(
        result.recovered
          ? "반영 결과를 확인하지 못했습니다.\n목록을 다시 불러왔습니다."
          : "반영 결과를 확인하지 못했습니다.\n목록도 불러오지 못해 다시 확인이 필요합니다."
      );
      return;
    }

    const { outcome } = result;
    if (result.verifyFailed) {
      // 검증 스캔 실패 — 열린 모달이 "이상 없음"으로 보이면 실패 배너가 그 뒤에 숨는다.
      // 모달을 닫아 실패 상태를 드러낸다(가시적 실패 계약). 행별 결과(성공/충돌/실패)는
      // RPC 응답으로 이미 확정된 사실이므로 목록 확인 실패와 무관하게 함께 보고한다
      // (구현 델타 합의 — 검증 실패가 알려진 결과를 삼키지 않는다).
      setModalOpen(false);
      setChecked(new Map());
      if (outcome.applied > 0) {
        toast.success(`배송정보 ${outcome.applied}건의 상품명을 반영했습니다.`);
      }
      if (outcome.conflicts > 0) {
        toast.warning(
          `반영하지 못한 배송정보 ${outcome.conflicts}건이 있습니다.\n다른 곳에서 수정되었거나 주문이 바뀐 건입니다.`
        );
      }
      if (outcome.failed > 0) {
        toast.error(`반영에 실패한 배송정보 ${outcome.failed}건이 있습니다.`);
      }
      toast.error("반영 후 목록 확인에 실패했습니다.\n다시 확인이 필요합니다.");
      return;
    }
    // 반영된 행은 선택에서 제거. 미반영(conflict) 행은 검증 스캔이 내용을 갱신하므로
    // 대부분 지문 정리 effect가 체크를 해제한다 — 내용이 그대로인 행만 살아남는다(안전 방향).
    setChecked((prev) => {
      const next = new Map(prev);
      outcome.appliedRows.forEach((r) => next.delete(r.parsedOrderId));
      return next;
    });
    if (outcome.applied > 0) {
      toast.success(`배송정보 ${outcome.applied}건의 상품명을 반영했습니다.`);
    }
    if (outcome.conflicts > 0) {
      toast.warning(
        `반영하지 못한 배송정보 ${outcome.conflicts}건이 있습니다.\n다른 곳에서 수정되었거나 주문이 바뀐 건입니다.`
      );
    }
    if (outcome.failed > 0) {
      toast.error(`반영에 실패한 배송정보 ${outcome.failed}건이 있습니다.\n목록에서 상태를 확인합니다.`);
    }
  }, [apply, checked, candidates]);

  if (!userId || !parseProductName) return null;

  return (
    <>
      <ShippingStaleSyncBanner
        staleCount={bannerCount}
        scanFailed={scanState === "failed"}
        isChecking={scanning}
        onOpen={handleOpen}
        onRetry={rescan}
      />
      <ShippingStaleSyncModal
        open={modalOpen}
        onOpenChange={handleOpenChange}
        candidates={displayCandidates}
        checked={checked}
        onToggle={handleToggle}
        onGroupSet={handleGroupSet}
        onApply={handleApply}
        isApplying={isApplying}
        applyReady={scanState === "ready"}
        orderDate={orderDate}
      />
    </>
  );
}
